import {
  Box,
  Button,
  Checkbox,
  CloseButton,
  Dialog,
  HStack,
  Heading,
  Portal,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import DateRangePicker from "../../../../components/DateRangePicker";
import SelectFilter from "../../../../components/SelectFilter";
import { toaster } from "../../../../components/ui/toaster";
import { useMerchant } from "../../../../hooks/useMerchant";
import { useTRPC } from "../../../../trpc/client";
import { CreateReconciliationFile } from "../../../../types/types";

type Props = {
  open: boolean;
  onClose: () => void;
};

const initialFileParams: CreateReconciliationFile = {
  rentalsMerchantId: "",
  type: "payment-date",
  startDate: "",
  endDate: "",
  format: "excel-palomma",
  creationMethod: ["manual", "bulk", "erp", "api"],
  erpSyncStatus: [
    "success",
    "error",
    "none",
    "manual",
    "retry",
    "off_hours",
    "processing",
  ],
};

const DownloadReportModal = ({ open, onClose }: Props): JSX.Element => {
  const trpc = useTRPC();
  const [fileParams, setFileParams] =
    useState<CreateReconciliationFile>(initialFileParams);
  const [reconciliationReference, setReconciliationReference] = useState<
    string | null
  >(null);
  const [reconciliationFormat, setReconciliationFormat] = useState<
    CreateReconciliationFile["format"] | null
  >(null);
  const [pollingStartedAt, setPollingStartedAt] = useState<number | null>(null);
  const { merchant } = useMerchant();

  const createReconciliationFile = useMutation({
    ...trpc.settlements.createReconciliationFile.mutationOptions(),
    onSuccess: (result, variables) => {
      setReconciliationReference(result.reference);
      setReconciliationFormat(variables.format);
      setPollingStartedAt(Date.now());
      toaster.create({
        title: "Generando archivo",
        description:
          "Estamos generando tu reporte. Te avisaremos cuando termine.",
        type: "warning",
        duration: 4000,
        meta: { closable: true },
      });
      setFileParams(initialFileParams);
      onClose();
    },
    onError: (error) => {
      console.error("Error creating reconciliation file:", error);
      toaster.create({
        title: "Error",
        description: "Hubo un error al crear el archivo de reconciliación.",
        type: "error",
        duration: 5000,
        meta: { closable: true },
      });
    },
  });

  const reconciliationStatusQuery = useQuery({
    ...trpc.settlements.getReconciliationFileStatus.queryOptions({
      rentalsMerchantId: merchant?.id || "",
      reference: reconciliationReference || "",
      format: reconciliationFormat || "excel-palomma",
    }),
    enabled:
      !!merchant?.id && !!reconciliationReference && !!reconciliationFormat,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.status === "complete") return false;
      return 2500;
    },
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (reconciliationStatusQuery.data?.status === "complete") {
      // More reliable than synthetic click in async contexts.
      window.location.assign(reconciliationStatusQuery.data.downloadUrl);

      toaster.create({
        title: "Archivo listo",
        description: "El archivo se genero y se descargo automaticamente.",
        type: "success",
        duration: 5000,
        meta: { closable: true },
      });

      setReconciliationReference(null);
      setReconciliationFormat(null);
      setPollingStartedAt(null);
    }
  }, [reconciliationStatusQuery.data]);

  useEffect(() => {
    if (!reconciliationStatusQuery.isError) return;
    toaster.create({
      title: "Error",
      description:
        "Hubo un problema consultando el estado del reporte. Intenta nuevamente.",
      type: "error",
      duration: 5000,
      meta: { closable: true },
    });
    setReconciliationReference(null);
    setReconciliationFormat(null);
    setPollingStartedAt(null);
  }, [reconciliationStatusQuery.isError]);

  useEffect(() => {
    if (!reconciliationReference || !pollingStartedAt) return;
    if (reconciliationStatusQuery.data?.status === "complete") return;

    const timeoutId = window.setTimeout(() => {
      toaster.create({
        title: "Tiempo de espera agotado",
        description:
          "La generacion del reporte esta tardando mas de lo esperado. Puedes intentarlo de nuevo.",
        type: "warning",
        duration: 6000,
        meta: { closable: true },
      });
      setReconciliationReference(null);
      setReconciliationFormat(null);
      setPollingStartedAt(null);
    }, 10 * 60 * 1000);

    return () => window.clearTimeout(timeoutId);
  }, [
    reconciliationReference,
    pollingStartedAt,
    reconciliationStatusQuery.data?.status,
  ]);

  const handleDateRangeChange = (dates: [string, string] | undefined) => {
    setFileParams({
      ...fileParams,
      startDate: dates?.[0] ?? "",
      endDate: dates?.[1] ?? "",
    });
  };

  const handleSubmit = () => {
    if (!merchant?.id) return;
    createReconciliationFile.mutate({
      ...fileParams,
      rentalsMerchantId: merchant.id,
    });
  };

  const handleClose = () => {
    setFileParams(initialFileParams);
    onClose();
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(e) => !e.open && handleClose()}
      size="xl"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content borderRadius="md" maxWidth="32rem">
            <Dialog.Header borderBottom="1px solid #ebecef80" padding="4">
              <Dialog.Title>
                <Heading>Reporte de pagos</Heading>
              </Dialog.Title>
            </Dialog.Header>
            <Dialog.CloseTrigger asChild>
              <CloseButton marginTop="1" onClick={handleClose} />
            </Dialog.CloseTrigger>
            <Dialog.Body py={6}>
              <VStack gap={4} align="stretch">
                <Box>
                  <Text fontWeight={600} mb={2}>
                    Tipo
                  </Text>
                  <SelectFilter
                    options={["Fecha de Pago", "Fecha de Abono"]}
                    selectedOption={
                      fileParams.type === "payment-date"
                        ? "Fecha de Pago"
                        : "Fecha de Abono"
                    }
                    onSelect={(type) =>
                      setFileParams({
                        ...fileParams,
                        type:
                          type === "Fecha de Pago"
                            ? "payment-date"
                            : "settlement-date",
                      })
                    }
                    width="100%"
                    isClearable={false}
                  />
                </Box>
                <Box>
                  <Text fontWeight={600} mb={2}>
                    Fechas
                  </Text>
                  <DateRangePicker
                    placeholder="Seleccionar"
                    setValues={(dates) => handleDateRangeChange(dates)}
                    values={[fileParams.startDate, fileParams.endDate]}
                  />
                </Box>

                <Box>
                  <Text fontWeight={600} mb={2}>
                    Formato
                  </Text>
                  <SelectFilter
                    options={[
                      "Excel Palomma",
                      "Bancolombia Final",
                      "Asobancaria",
                      "Nuwwe",
                      "PUTTY",
                      "Netgeo",
                    ]}
                    selectedOption={
                      (fileParams.format === "bancolombia-final" &&
                        "Bancolombia Final") ||
                      (fileParams.format === "asobancaria" && "Asobancaria") ||
                      (fileParams.format === "nuwwe" && "Nuwwe") ||
                      (fileParams.format === "putty" && "PUTTY") ||
                      (fileParams.format === "netgeo" && "Netgeo") ||
                      "Excel Palomma"
                    }
                    onSelect={(format) =>
                      setFileParams({
                        ...fileParams,
                        format:
                          (format === "Bancolombia Final" &&
                            "bancolombia-final") ||
                          (format === "Asobancaria" && "asobancaria") ||
                          (format === "Nuwwe" && "nuwwe") ||
                          (format === "PUTTY" && "putty") ||
                          (format === "Netgeo" && "netgeo") ||
                          "excel-palomma",
                      })
                    }
                    width="100%"
                    isClearable={false}
                  />
                </Box>
                <Box>
                  <Text fontWeight={600} mb={2}>
                    Tipo de creación de facturas
                  </Text>
                  <HStack align="start" gap={8}>
                    <Checkbox.Root
                      checked={fileParams.creationMethod.includes("manual")}
                      onCheckedChange={() =>
                        setFileParams({
                          ...fileParams,
                          creationMethod: fileParams.creationMethod.includes(
                            "manual"
                          )
                            ? fileParams.creationMethod.filter(
                                (method) => method !== "manual"
                              )
                            : [...fileParams.creationMethod, "manual"],
                        })
                      }
                    >
                      <Checkbox.HiddenInput />
                      <Checkbox.Control />
                      <Checkbox.Label>Manual</Checkbox.Label>
                    </Checkbox.Root>
                    <Checkbox.Root
                      checked={fileParams.creationMethod.includes("bulk")}
                      onCheckedChange={() =>
                        setFileParams({
                          ...fileParams,
                          creationMethod: fileParams.creationMethod.includes(
                            "bulk"
                          )
                            ? fileParams.creationMethod.filter(
                                (method) => method !== "bulk"
                              )
                            : [...fileParams.creationMethod, "bulk"],
                        })
                      }
                    >
                      <Checkbox.HiddenInput />
                      <Checkbox.Control />
                      <Checkbox.Label>Archivo</Checkbox.Label>
                    </Checkbox.Root>
                    <Checkbox.Root
                      checked={fileParams.creationMethod.includes("erp")}
                      onCheckedChange={() =>
                        setFileParams({
                          ...fileParams,
                          creationMethod: fileParams.creationMethod.includes(
                            "erp"
                          )
                            ? fileParams.creationMethod.filter(
                                (method) => method !== "erp"
                              )
                            : [...fileParams.creationMethod, "erp"],
                        })
                      }
                    >
                      <Checkbox.HiddenInput />
                      <Checkbox.Control />
                      <Checkbox.Label>Automático (ERP)</Checkbox.Label>
                    </Checkbox.Root>
                    <Checkbox.Root
                      checked={fileParams.creationMethod.includes("api")}
                      onCheckedChange={() =>
                        setFileParams({
                          ...fileParams,
                          creationMethod: fileParams.creationMethod.includes(
                            "api"
                          )
                            ? fileParams.creationMethod.filter(
                                (method) => method !== "api"
                              )
                            : [...fileParams.creationMethod, "api"],
                        })
                      }
                    >
                      <Checkbox.HiddenInput />
                      <Checkbox.Control />
                      <Checkbox.Label>API</Checkbox.Label>
                    </Checkbox.Root>
                  </HStack>
                </Box>
                <Box>
                  <Text fontWeight={600} mb={2}>
                    Contabilización automática en ERP
                  </Text>
                  <HStack gap={16}>
                    <VStack align="start">
                      <Checkbox.Root
                        checked={fileParams.erpSyncStatus.includes("success")}
                        onCheckedChange={() =>
                          setFileParams({
                            ...fileParams,
                            erpSyncStatus: fileParams.erpSyncStatus.includes(
                              "success"
                            )
                              ? fileParams.erpSyncStatus.filter(
                                  (status) => status !== "success"
                                )
                              : [...fileParams.erpSyncStatus, "success"],
                          })
                        }
                      >
                        <Checkbox.HiddenInput />
                        <Checkbox.Control />
                        <Checkbox.Label>Automática</Checkbox.Label>
                      </Checkbox.Root>
                      <Checkbox.Root
                        checked={fileParams.erpSyncStatus.includes("retry")}
                        onCheckedChange={() =>
                          setFileParams({
                            ...fileParams,
                            erpSyncStatus: fileParams.erpSyncStatus.includes(
                              "retry"
                            )
                              ? fileParams.erpSyncStatus.filter(
                                  (status) => status !== "retry"
                                )
                              : [...fileParams.erpSyncStatus, "retry"],
                          })
                        }
                      >
                        <Checkbox.HiddenInput />
                        <Checkbox.Control />
                        <Checkbox.Label>Reintento Exitoso</Checkbox.Label>
                      </Checkbox.Root>
                      <Checkbox.Root
                        checked={fileParams.erpSyncStatus.includes("manual")}
                        onCheckedChange={() =>
                          setFileParams({
                            ...fileParams,
                            erpSyncStatus: fileParams.erpSyncStatus.includes(
                              "manual"
                            )
                              ? fileParams.erpSyncStatus.filter(
                                  (status) => status !== "manual"
                                )
                              : [...fileParams.erpSyncStatus, "manual"],
                          })
                        }
                      >
                        <Checkbox.HiddenInput />
                        <Checkbox.Control />
                        <Checkbox.Label>Manual</Checkbox.Label>
                      </Checkbox.Root>
                    </VStack>
                    <VStack align="start">
                      <Checkbox.Root
                        checked={fileParams.erpSyncStatus.includes("off_hours")}
                        onCheckedChange={() =>
                          setFileParams({
                            ...fileParams,
                            erpSyncStatus: fileParams.erpSyncStatus.includes(
                              "off_hours"
                            )
                              ? fileParams.erpSyncStatus.filter(
                                  (status) => status !== "off_hours"
                                )
                              : [...fileParams.erpSyncStatus, "off_hours"],
                          })
                        }
                      >
                        <Checkbox.HiddenInput />
                        <Checkbox.Control />
                        <Checkbox.Label>Pendiente</Checkbox.Label>
                      </Checkbox.Root>
                      <Checkbox.Root
                        checked={fileParams.erpSyncStatus.includes(
                          "processing"
                        )}
                        onCheckedChange={() =>
                          setFileParams({
                            ...fileParams,
                            erpSyncStatus: fileParams.erpSyncStatus.includes(
                              "processing"
                            )
                              ? fileParams.erpSyncStatus.filter(
                                  (status) => status !== "processing"
                                )
                              : [...fileParams.erpSyncStatus, "processing"],
                          })
                        }
                      >
                        <Checkbox.HiddenInput />
                        <Checkbox.Control />
                        <Checkbox.Label>En Proceso</Checkbox.Label>
                      </Checkbox.Root>
                      <Checkbox.Root
                        checked={fileParams.erpSyncStatus.includes("error")}
                        onCheckedChange={() =>
                          setFileParams({
                            ...fileParams,
                            erpSyncStatus: fileParams.erpSyncStatus.includes(
                              "error"
                            )
                              ? fileParams.erpSyncStatus.filter(
                                  (status) => status !== "error"
                                )
                              : [...fileParams.erpSyncStatus, "error"],
                          })
                        }
                      >
                        <Checkbox.HiddenInput />
                        <Checkbox.Control />
                        <Checkbox.Label>Con Error</Checkbox.Label>
                      </Checkbox.Root>
                    </VStack>
                  </HStack>
                </Box>
                <HStack gap={4} mt={4}>
                  <Button variant="outline" onClick={handleClose} flexGrow={1}>
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    loading={createReconciliationFile.isPending}
                    flexGrow={1}
                    disabled={
                      !merchant?.id ||
                      fileParams.startDate === "" ||
                      fileParams.endDate === "" ||
                      !fileParams.creationMethod.length ||
                      !fileParams.erpSyncStatus.length
                    }
                  >
                    Generar
                  </Button>
                </HStack>
              </VStack>
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default DownloadReportModal;
